import React from 'react'
import { observer } from 'mobx-react'
import { UserService } from '../../services'
import { AppState } from '../../stores/app'
import './register-preview-device.scss'

import { arrayUnique } from '../../_utils/utils'
import { getPushlySdk } from '../../_utils/pushly-sdk'
import { useService } from '@pushly/aqe/lib/hooks'
import { BetterSelect } from '@pushly/aqe/lib/components/better-select/better-select'
import { Button, PageHeader, Select } from '@pushly/aqe/lib/components'
import { Flex, Space, Tooltip, notification } from 'antd'

type RegisterPreviewDeviceState = {
    permission: string
    availableDomains: any[]
    selectedDomains: number[]
    existingSelectedDomains: number[]
}

export const RegisterPreviewDevice: React.FC = observer(() => {
    const appState = useService(AppState)
    const userService = useService(UserService)

    const [growl, growlContextHolder] = notification.useNotification()
    const showNotification = (method: 'success' | 'error', msg: string) => {
        growl[method]({
            message: '',
            description: msg,
        })
    }

    const [state, setState] = React.useState<RegisterPreviewDeviceState>({
        permission: 'default',
        availableDomains: [],
        selectedDomains: [],
        existingSelectedDomains: [],
    })

    React.useEffect(() => {
        if (appState.currentDomainJsonData) {
            setDomainsState()
            setPermissionState()
        }
    }, [appState.currentDomainJsonData])

    const sdk = getPushlySdk()
    const user = appState.currentUser! || {}
    const domain = appState.currentDomain! || {}
    const subStatus = !sdk?.context.user ? 'unknown' : state.permission

    const setPermissionState = (): void => {
        if (sdk) {
            if (!sdk.context.user) {
                setTimeout(() => setPermissionState(), 50)
                return
            }

            const permission = getSdkUserSubStatus()

            if (permission === 'default') {
                sdk.on('permission_denied', () => handlePermissionChange('denied'))
                sdk.on('permission_dismissed', () => handlePermissionChange('denied'))
                sdk.on('permission_allowed', () => handlePermissionChange('subscribed'))
                sdk.PushNotifications.showPermissionPrompt()
            }

            setState((currentState) => ({
                ...currentState,
                permission,
            }))
        } else {
            showNotification('error', 'We are unable to resolve your current Notification permissions.')
        }
    }

    const setDomainsState = async (): Promise<void> => {
        const userDomains = appState.currentUserDomains

        if (user && domain) {
            // re-fetch user to ensure puuids are up to date
            const { data: userRecord } = await userService.fetchById(appState.currentUser!.id)
            const puuids = userRecord?.puuids ?? user.puuids ?? []
            const userRegistrationDomainIds = puuids.map((r) => r.domainId)

            const availableDomains = (userDomains || []).map((d) => ({
                id: d.id,
                name: d.displayName,
            }))

            const selectedDomains = arrayUnique([domain.id, ...userRegistrationDomainIds])

            setState((currentState) => ({
                ...currentState,
                availableDomains,
                selectedDomains: [],
                existingSelectedDomains: [...selectedDomains],
            }))
        }
    }

    const handlePermissionChange = (permission: string): void => {
        setState((currentState) => ({ ...currentState, permission }))

        if (permission === 'subscribed') {
            registerPuuids()
        }
    }

    const handleSelectedDomainsChange = (selectedDomains: number[]): void => {
        setState((currentState) => ({ ...currentState, selectedDomains }))
    }

    const registerPuuids = async (): Promise<void> => {
        const sdkUser = sdk?.context.user
        if (!sdkUser) {
            console.error('Unable to register puuids')
            return
        }

        const appUser = appState.currentUser!

        const registrations: Array<Promise<any>> = []
        const completedDomainIds: number[] = []
        const failedDomainIds: number[] = []

        state.selectedDomains.forEach((domainId) => {
            registrations.push(
                userService
                    .registerPuuid(appUser.id, domainId, sdkUser.puuid)
                    .then(() => completedDomainIds.push(domainId))
                    .catch(() => failedDomainIds.push(domainId)),
            )
        })

        await Promise.all(registrations)

        setState((currentState) => ({
            ...currentState,
            existingSelectedDomains: [...state.selectedDomains, ...completedDomainIds],
            selectedDomains: [...failedDomainIds],
        }))

        if (failedDomainIds.length) {
            showNotification('error', 'Unable to complete registration for selected domains. Please try again.')
        } else {
            showNotification('success', 'Domain registrations updated successfully.')
        }
    }

    const getSdkUserSubStatus = (): string => {
        return getPushlySdk()?.context.user.subscriberState ?? 'default'
    }

    let message: React.ReactNode
    if (subStatus === 'subscribed') {
        message = (
            <>
                <p>
                    This device has been successfully linked to {user.name || user.email} for notification previews on{' '}
                    {domain.displayName}.
                    <span className="close-notice">
                        {' '}
                        You may now close this window and return to the platform to begin sending previews to this
                        device.
                    </span>
                </p>

                <p>
                    To register this device to another domain please update the domain selections below and click
                    "Update Domain Registrations".
                </p>
            </>
        )
    } else if (subStatus === 'denied') {
        message = (
            <>
                <p>
                    This device has blocked notification permissions for pushly.com. Please manually reset your browser
                    permissions, reload the page, and accept the requested permission to begin delivering previews to
                    this device.
                </p>
            </>
        )
    } else {
        message = (
            <>
                <p>To enable Web Push on this device please accept browser permissions.</p>
            </>
        )
    }

    if (appState.appIsLoading) {
        return null
    }

    const domainOptions = state.availableDomains.map((d) => ({
        value: d.id,
        label: d.name,
        disabled: state.existingSelectedDomains.indexOf(d.id) !== -1,
    }))

    console.debug('domainOptions', domainOptions)
    return (
        <>
            {growlContextHolder}
            <div className="register-preview-device">
                <div className="message">
                    <div className="pushly-logo" />

                    {window.PlatformNotifications?.supported ? (
                        message
                    ) : (
                        <div>This page is only valid for devices that support Web Push.</div>
                    )}

                    {subStatus === 'subscribed' && state.availableDomains.length > 1 && (
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <Select
                                mode="multiple"
                                className="domain-registration-select"
                                // dropdownClassName="domain-registration-options"
                                options={domainOptions}
                                onChange={handleSelectedDomainsChange}
                                value={state.selectedDomains}
                                selectAllLabel="All Domains"
                                showSelectAll={false}
                                showSearch={false}
                                maxTagCount={5}
                                maxTagPlaceholder={(options: any[]) => {
                                    const multi = options.length > 1
                                    return (
                                        <Tooltip title={options.map((o) => o.label).join(', ')}>
                                            {options.length} {multi ? 'Domains' : 'Domain'} Selected
                                        </Tooltip>
                                    )
                                }}
                                style={{ width: '100%' }}
                            />

                            <Button type="primary" onClick={registerPuuids} disabled={!state.selectedDomains.length}>
                                <span>Update Domain Registrations</span>
                            </Button>
                        </Space>
                    )}
                </div>
            </div>
        </>
    )
})
